/* src/App.js */
import React, { useEffect, useMemo, useState } from "react";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const IconBack = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const IconNext = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const IconUpload = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 8l-4-4m4 4l4-4"
    />
  </svg>
);

const IconTrash = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-4 0a1 1 0 00-1 1v1h6V4a1 1 0 00-1-1m-4 0h4"
    />
  </svg>
);

// --- helpers ---
const uid = (p = "") => p + Math.random().toString(36).slice(2, 9);

// Format a Date object to YYYY-MM-DD using local time (fixes timezone off-by-one)
function formatDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const todayLocalISO = () => formatDateLocal(new Date());

const numberOrZero = (v) => {
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// manual P&L only (user-entered)
function computeTradePL(trade) {
  if (!trade) return 0;
  if (trade.pnl === undefined || String(trade.pnl).trim() === "") return 0;
  return Math.round(numberOrZero(trade.pnl) * 100) / 100;
}

function getMonthGrid(y, m) {
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const start = first.getDay();
  const total = last.getDate();
  const weeks = [];
  let day = 1 - start;
  while (day <= total) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if (day < 1 || day > total) week.push(null);
      else week.push(new Date(y, m, day));
      day++;
    }
    weeks.push(week);
  }
  return weeks;
}

// Convert various time formats to 24h "HH:MM" for <input type="time">
function to24Hour(value) {
  if (!value) return "";
  const s = String(value).trim();
  // already HH:MM
  const hhmm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    let hh = Number(hhmm[1]);
    const mm = hhmm[2];
    if (hh >= 0 && hh < 24) return `${String(hh).padStart(2, "0")}:${mm}`;
  }
  // with am/pm e.g. "8:30 am" or "08:30PM"
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (ampm) {
    let hh = Number(ampm[1]);
    const mm = ampm[2];
    const suf = ampm[3].toLowerCase();
    if (suf === "pm" && hh !== 12) hh += 12;
    if (suf === "am" && hh === 12) hh = 0;
    return `${String(hh).padStart(2, "0")}:${mm}`;
  }
  // no parse -> return empty to avoid invalid input value
  return "";
}

// Format stored value (HH:MM or "8:30 am") into a friendly "h:mm AM/PM"
function formatTime12h(value) {
  if (!value) return "";
  const s = String(value).trim();
  // if already contains AM/PM just normalize spacing/caps
  if (/[AaPp][Mm]$/.test(s)) {
    return s.toUpperCase().replace(/\s+/g, " ");
  }
  const hhmm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    let hh = Number(hhmm[1]);
    const mm = hhmm[2];
    const suffix = hh >= 12 ? "PM" : "AM";
    const hour12 = ((hh + 11) % 12) + 1;
    return `${hour12}:${mm} ${suffix}`;
  }
  return s;
}

// --- App ---
export default function App() {
  const [days, setDays] = useState(() => {
    try {
      const raw = localStorage.getItem("tj_days_v1");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // IMPORTANT: default selectedDate is null so no day opens automatically
  const [selectedDate, setSelectedDate] = useState(null);
  const [yearMonth, setYearMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [imageModal, setImageModal] = useState(null);

  // Interval for P&L chart: "month" | "year" | "5y" | "all"
  const [interval, setInterval] = useState("month");

  useEffect(() => {
    localStorage.setItem("tj_days_v1", JSON.stringify(days));
  }, [days]);

  // Day helpers
  const getDayByDate = (date) => days.find((d) => d.date === date) || null;
  const createDay = (date) => {
    const d = {
      id: uid("day-"),
      date,
      startBalance: "",
      morningNote: "",
      eveningNote: "",
      trades: [],
    };
    setDays((s) => [d, ...s]);
    setSelectedDate(date);
    return d;
  };
  const updateDay = (updated) => {
    setDays((s) => {
      const exists = s.find((p) => p.date === updated.date);
      if (exists) return s.map((p) => (p.date === updated.date ? updated : p));
      return [updated, ...s];
    });
  };
  const deleteDay = (date) => {
    if (!window.confirm("Delete this day and all trades?")) return;
    setDays((s) => s.filter((d) => d.date !== date));
    setSelectedDate(null);
  };

  // day P&L = sum of manual trade P&Ls
  const computeDayPL = (day) => {
    if (!day) return 0;
    const sum = (day.trades || []).reduce((a, t) => a + computeTradePL(t), 0);
    return Math.round(sum * 100) / 100;
  };

  const computeEndBalance = (day) => {
    const start = numberOrZero(day.startBalance);
    return Math.round((start + computeDayPL(day)) * 100) / 100;
  };

  const monthGrid = useMemo(() => getMonthGrid(yearMonth.y, yearMonth.m), [yearMonth]);

  // analytics
  const stats = useMemo(() => {
    const allTrades = days.flatMap((d) => (d.trades || []).map((t) => ({ ...t, date: d.date })));
    const totalPL = Math.round(days.reduce((s, d) => s + computeDayPL(d), 0) * 100) / 100;
    const wins = allTrades.filter((t) => computeTradePL(t) > 0).length;
    const losses = allTrades.filter((t) => computeTradePL(t) < 0).length;
    const breakeven = allTrades.filter((t) => computeTradePL(t) === 0).length;
    const totalTrades = allTrades.length;
    const accuracy = totalTrades ? Math.round((wins / totalTrades) * 1000) / 10 : 0;
    const dayPLs = days
      .slice()
      .sort((a, b) => (a.date > b.date ? 1 : -1))
      .map((d) => ({ date: d.date, pl: computeDayPL(d) }));

    // Account balance — end balance of the latest (present) day (if any)
    let accBalance = 0;
    if (days.length) {
      const latest = days
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .pop();
      if (latest) accBalance = computeEndBalance(latest);
    }

    return { totalPL, wins, losses, breakeven, totalTrades, accuracy, dayPLs, accBalance };
  }, [days]);

  // Interval filtering for line chart
  const filteredDayPLs = useMemo(() => {
    const sorted = stats.dayPLs.slice().sort((a, b) => new Date(a.date) - new Date(b.date));

    if (interval === "all") return sorted;

    if (interval === "1d") {
      const targetDate = selectedDate || todayLocalISO();
      const day = days.find((d) => d.date === targetDate);
      if (!day) return [];
      return (day.trades || []).map((t, i) => ({
        date: `${targetDate} #${i + 1}`,
        pl: computeTradePL(t),
      }));
    }

    if (interval === "7d") {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 6);
      cutoff.setHours(0, 0, 0, 0);
      return sorted.filter((d) => {
        const dt = new Date(d.date);
        dt.setHours(0, 0, 0, 0);
        return dt >= cutoff;
      });
    }

    if (interval === "30d") {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 29);
      cutoff.setHours(0, 0, 0, 0);
      return sorted.filter((d) => {
        const dt = new Date(d.date);
        dt.setHours(0, 0, 0, 0);
        return dt >= cutoff;
      });
    }

    if (interval === "month") {
      const start = new Date(yearMonth.y, yearMonth.m, 1);
      const end = new Date(yearMonth.y, yearMonth.m + 1, 0);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return sorted.filter((d) => {
        const dt = new Date(d.date);
        return dt >= start && dt <= end;
      });
    }

    const cutoff = new Date();
    if (interval === "year") cutoff.setFullYear(cutoff.getFullYear() - 1);
    if (interval === "5y") cutoff.setFullYear(cutoff.getFullYear() - 5);
    cutoff.setHours(0, 0, 0, 0);
    return sorted.filter((d) => new Date(d.date) >= cutoff);
  }, [stats.dayPLs, interval, yearMonth, days, selectedDate]);

  // charts
  const lineData = {
    labels: filteredDayPLs.map((d) => d.date),
    datasets: [
      {
        label: "Day P&L",
        data: filteredDayPLs.map((d) => d.pl),
        tension: 0.25,
        fill: false,
        borderWidth: 2,
        backgroundColor: "rgba(99,102,241,0.6)",
        borderColor: "rgba(99,102,241,0.95)",
      },
    ],
  };
  const pieData = {
    labels: ["Wins", "Losses", "Break-even"],
    datasets: [
      {
        data: [stats.wins, stats.losses, stats.breakeven],
        backgroundColor: ["#22c55e", "#ef4444", "#9ca3af"], // green, red, gray
        hoverOffset: 6,
      },
    ],
  };

  function prevMonth() {
    setYearMonth((ym) => {
      const d = new Date(ym.y, ym.m - 1, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }
  function nextMonth() {
    setYearMonth((ym) => {
      const d = new Date(ym.y, ym.m + 1, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }

  // DayDetail component (full detailed modal)
  function DayDetail({ dateStr, onClose }) {
    const original =
      getDayByDate(dateStr) || {
        id: uid("day-"),
        date: dateStr,
        startBalance: "",
        morningNote: "",
        eveningNote: "",
        trades: [],
      };
    const [local, setLocal] = useState(original);
    const [openAnim, setOpenAnim] = useState(false);

    useEffect(() => {
      setLocal(original);
      const t = setTimeout(() => setOpenAnim(true), 10);
      return () => clearTimeout(t);
    }, [dateStr]); // eslint-disable-line

    // ESC key handler to close DayDetail
    useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      if (imageModal) {
        setImageModal(null);
      } else {
        onClose();
      }
    }
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, [imageModal, onClose]);


    function addTrade() {
      const t = {
        id: uid("t-"),
        assetName: "",
        dir: "Buy",
        open: "",
        close: "",
        riskReward: "",
        pnl: "",
        rating: "",
        image: null,
        result: "",
        summary: "",
        time: "", // now stored as "HH:MM"
        status: "Closed",
        positionSize: "",
      };
      setLocal((l) => ({ ...l, trades: [...l.trades, t] }));
    }

    function updateTradeAt(idx, patch) {
      setLocal((l) => {
        const next = { ...l, trades: l.trades.map((x) => ({ ...x })) };
        const t = { ...next.trades[idx], ...patch };

        // if pnl changed, auto update result (Win/Loss/Break-even)
        if (Object.prototype.hasOwnProperty.call(patch, "pnl")) {
          const val = String(patch.pnl).trim();
          if (val === "") t.result = "";
          else {
            const n = numberOrZero(patch.pnl);
            t.result = n > 0 ? "Win" : n < 0 ? "Loss" : "Break-even";
          }
        }

        next.trades[idx] = t;
        return next;
      });
    }

    function removeTrade(idx) {
      setLocal((l) => ({ ...l, trades: l.trades.filter((_, i) => i !== idx) }));
    }

    function saveDay() {
      updateDay(local);
      alert("Saved");
    }

    const dayPL = computeDayPL(local);
    const endBalance = computeEndBalance(local);

    function handleImageFile(idx, file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => updateTradeAt(idx, { image: ev.target.result });
      reader.readAsDataURL(file);
    }

    const ratingTextClass = (r) => {
      if (r === "A+") return "text-yellow-400";
      if (r === "A") return "text-slate-300";
      if (r === "B") return "text-red-400";
      return "text-slate-200";
    };

    const pnlInputColor = (pnl) => {
      if (pnl === undefined || String(pnl).trim() === "") return "text-slate-200";
      const n = numberOrZero(pnl);
      return n > 0 ? "text-emerald-300" : n < 0 ? "text-rose-300" : "text-slate-400";
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/60 transition-opacity"
          onClick={() => {
            setOpenAnim(false);
            setTimeout(() => onClose(), 180);
          }}
          aria-hidden
        />
        <div
          className={`relative z-60 w-full max-w-7xl bg-gradient-to-b from-slate-900 to-slate-800 text-slate-50 rounded-2xl shadow-2xl overflow-auto max-h-[92vh] p-6 transform transition-all duration-300 ${
            openAnim ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
          role="dialog"
        >
          <div className="flex items-start justify-between gap-4">
            {/* BACK ARROW + DATE */}
            <div className="flex items-center gap-4">
              <button
                aria-label="Back"
                onClick={() => {
                  setOpenAnim(false);
                  setTimeout(() => onClose(), 140);
                }}
                className="text-slate-300 hover:text-slate-100 px-2 py-1 rounded"
                title="Back (Esc)"
              >
                <IconBack />
              </button>

              <div>
                <h2 className="text-2xl font-semibold tracking-tight">{local.date}</h2>
                <div className="text-sm mt-1">
                  Day P&L:&nbsp;
                  <span className={`font-mono ${dayPL > 0 ? "text-emerald-300" : dayPL < 0 ? "text-rose-300" : "text-slate-400"}`}>
                    {dayPL >= 0 ? `+$${dayPL}` : `$${dayPL}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <button
                className="px-3 py-1 border rounded border-slate-700 text-slate-200 hover:bg-slate-700"
                onClick={() => {
                  navigator.clipboard?.writeText(JSON.stringify(local));
                  alert("Copied JSON");
                }}
              >
                Copy JSON
              </button>
              <button
                className="px-3 py-1 bg-rose-600 text-white rounded hover:bg-rose-500"
                onClick={() => {
                  if (window.confirm("Delete this day?")) {
                    deleteDay(dateStr);
                    onClose();
                  }
                }}
              >
                Delete Day
              </button>
            </div>
          </div>

          {/* balances & notes */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-300">Start Balance</label>
              <input
                value={local.startBalance}
                onChange={(e) => setLocal({ ...local, startBalance: e.target.value })}
                className="mt-1 w-full p-2 rounded bg-slate-700 text-slate-100 border-none text-sm"
                placeholder="e.g. 10000"
              />
            </div>

            <div>
              <label className="text-sm text-slate-300">End Balance (auto)</label>
              <input value={String(endBalance)} disabled className="mt-1 w-full p-2 rounded bg-slate-800 text-slate-200 border-none text-sm" />
            </div>

            <div className="col-span-2">
              <label className="text-sm text-slate-300">Morning note</label>
              <textarea
                value={local.morningNote}
                onChange={(e) => setLocal({ ...local, morningNote: e.target.value })}
                className="mt-1 w-full p-3 rounded bg-slate-700 text-slate-100 h-20 border-none text-sm"
                placeholder="How the day started..."
              />
            </div>
          </div>

          {/* trades header */}
          <div className="mt-6 flex items-center justify-between border-t border-slate-700 pt-4">
            <h3 className="text-lg font-medium">Trades</h3>
            <div className="flex gap-2">
              <button className="px-3 py-1 bg-emerald-600 text-black rounded font-medium" onClick={addTrade}>
                Add Trade
              </button>
            </div>
          </div>

          {/* trades table (horizontal scroll only here) */}
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[1400px] w-full table-auto border-collapse">
              <thead>
                <tr className="text-xs text-slate-400 text-left">
                  <th className="px-2 py-2">#</th>
                  <th className="px-2 py-2">Asset</th>
                  <th className="px-2 py-2">Dir</th>
                  <th className="px-2 py-2">Open</th>
                  <th className="px-2 py-2">Close</th>
                  <th className="px-2 py-2">R:R</th>
                  <th className="px-2 py-2">Trade P&L</th>
                  <th className="px-2 py-2">Result</th>
                  <th className="px-2 py-2">Rating</th>
                  <th className="px-2 py-2">Summary</th>
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Time</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Pic</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(local.trades || []).map((t, idx) => {
                  const pnlNum = computeTradePL(t);
                  return (
                    <tr key={t.id} className="align-top border-b border-slate-700">
                      <td className="px-2 py-2 text-sm">{idx + 1}</td>

                      <td className="px-2 py-2">
                        <input
                          value={t.assetName}
                          onChange={(e) => updateTradeAt(idx, { ...t, assetName: e.target.value })}
                          className="w-36 p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none"
                          placeholder="e.g. BTCUSD"
                        />
                      </td>

                      <td className="px-2 py-2">
                        <button
                          onClick={() => updateTradeAt(idx, { ...t, dir: t.dir === "Buy" ? "Sell" : "Buy" })}
                          className={`text-xs px-2 py-1 rounded ${t.dir === "Buy" ? "bg-emerald-600 text-black" : "bg-rose-600 text-white"}`}
                        >
                          {t.dir || "Buy"}
                        </button>
                      </td>

                      <td className="px-2 py-2">
                        <input value={t.open} onChange={(e) => updateTradeAt(idx, { ...t, open: e.target.value })} className="w-20 p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none" placeholder="price" />
                      </td>

                      <td className="px-2 py-2">
                        <input value={t.close} onChange={(e) => updateTradeAt(idx, { ...t, close: e.target.value })} className="w-20 p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none" placeholder="price" />
                      </td>

                      <td className="px-2 py-2">
                        <input value={t.riskReward} onChange={(e) => updateTradeAt(idx, { ...t, riskReward: e.target.value })} className="w-20 p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none" placeholder="1:3" />
                      </td>

                      {/* P&L input: text type (no spinner), colored text only */}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className={`font-mono ${pnlInputColor(t.pnl)}`}>$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.pnl}
                            onChange={(e) => updateTradeAt(idx, { ...t, pnl: e.target.value })}
                            style={{ MozAppearance: "textfield", WebkitAppearance: "none" }}
                            className={`w-24 p-1 rounded bg-transparent text-sm focus:outline-none ${pnlInputColor(t.pnl)}`}
                          />
                        </div>
                      </td>

                      {/* Result derived from manual pnl */}
                      <td className="px-2 py-2">
                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pnlNum > 0 ? "bg-emerald-900 text-emerald-200" : pnlNum < 0 ? "bg-rose-900 text-rose-200" : "bg-amber-900 text-amber-200"}`}>
                          {t.pnl !== "" ? (pnlNum > 0 ? "Win" : pnlNum < 0 ? "Loss" : "Break-even") : "—"}
                        </div>
                      </td>

                      {/* Rating: default/transparent background, only text colored */}
                      <td className="px-2 py-2">
                        <select
                          value={t.rating || ""}
                          onChange={(e) => updateTradeAt(idx, { ...t, rating: e.target.value })}
                          className={`px-2 py-1 rounded text-sm border border-slate-700 bg-slate-800 text-slate-100 ${ratingTextClass(t.rating)}`}
                        >
                          <option value="">—</option>
                          <option value="A+">A+</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                        </select>
                      </td>

                      <td className="px-2 py-2">
                        <input value={t.summary} onChange={(e) => updateTradeAt(idx, { ...t, summary: e.target.value })} className="w-60 p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none" placeholder="short summary" />
                      </td>

                      {/* Date (auto-filled from day) */}
                      <td className="px-2 py-2">
                        <input disabled value={local.date} className="p-1 rounded bg-slate-800 text-slate-200 text-sm" />
                      </td>

                      {/* Time (proper time input + AM/PM display) */}
                      <td className="px-2 py-2">
  <input
    type="time"
    value={to24Hour(t.time)}
    onChange={(e) => updateTradeAt(idx, { ...t, time: e.target.value })}
    className="p-1 rounded bg-transparent text-slate-100 text-sm focus:outline-none"
  />
</td>


                      {/* Status (default background) */}
                      <td className="px-2 py-2">
                        <select
                          value={t.status || "Closed"}
                          onChange={(e) => updateTradeAt(idx, { ...t, status: e.target.value })}
                          className="px-2 py-1 rounded text-sm border border-slate-700 bg-slate-800 text-slate-100"
                        >
                          <option value="Running">Running</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>

                      {/* Pic */}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-12 bg-slate-700 rounded overflow-hidden flex items-center justify-center">
                            {t.image ? (
                              <img src={t.image} alt={`img-${idx}`} className="w-full h-full object-cover cursor-pointer" onClick={() => setImageModal({ src: t.image, alt: `${t.assetName || "Trade"} screenshot` })} />
                            ) : (
                              <div className="text-xs text-slate-500 px-1">No image</div>
                            )}
                          </div>

                          <div className="flex flex-col gap-1">
                            <label title="Upload" className="px-2 py-0.5 rounded bg-slate-700 cursor-pointer text-xs inline-flex items-center gap-1">
                              <span><IconUpload /></span>
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(idx, f); }} />
                            </label>
                            <button title="Delete" onClick={() => updateTradeAt(idx, { ...t, image: null })} className="px-2 py-0.5 rounded bg-rose-700 text-xs"><IconTrash /></button>
                          </div>
                        </div>
                      </td>

                      <td className="px-2 py-2">
                        <div className="flex gap-2">
                          <button className="px-2 py-1 text-xs border rounded border-slate-700" onClick={() => removeTrade(idx)}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* evening note */}
          <div className="mt-4">
            <label className="text-sm text-slate-300">Evening note / Wrap-up</label>
            <textarea value={local.eveningNote} onChange={(e) => setLocal({ ...local, eveningNote: e.target.value })} className="mt-1 w-full p-3 rounded bg-slate-700 border-none text-slate-100 h-24" placeholder="Wrap up your day..." />
          </div>

          {/* Save / Close */}
          <div className="mt-4 flex gap-2 justify-end">
            <button className="px-3 py-1 border rounded border-slate-700 text-slate-100" onClick={() => setLocal(original)}>Revert</button>
            <button className="px-3 py-1 bg-indigo-600 text-white rounded" onClick={saveDay}>Save</button>
            <button className="px-3 py-1 border rounded border-slate-700 text-slate-100" onClick={() => { setOpenAnim(false); setTimeout(() => onClose(), 140); }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // export helpers
  function exportJSON() {
    const data = JSON.stringify(days, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade-journal-${todayLocalISO()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportCSV() {
    const rows = [];
    rows.push(["Date", "StartBalance", "EndBalance", "Trade#", "Asset", "Direction", "Open", "Close", "R:R", "P&L", "Rating", "Summary", "Date", "Time", "Status"]);
    days.forEach((d) => {
      (d.trades || []).forEach((t, i) => {
        rows.push([d.date, d.startBalance || "", computeEndBalance(d) || "", i + 1, t.assetName || "", t.dir || "", t.open || "", t.close || "", t.riskReward || "", computeTradePL(t), t.rating || "", (t.summary || "").replace(/\n/g, " "), d.date, t.time || "", t.status || ""]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trade-journal-${todayLocalISO()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-black text-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Trade Journal</h1>
            <div className="text-sm text-slate-400 mt-1">Calendar • Click a day to open details</div>
          </div>

          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border rounded border-slate-700 text-slate-200" onClick={() => { if (window.confirm("Clear all data?")) { setDays([]); localStorage.removeItem("tj_days_v1"); } }}>Clear all</button>
            <button className="px-3 py-1 border rounded border-slate-700 text-slate-200" onClick={exportJSON}>Export JSON</button>
            <button className="px-3 py-1 border rounded border-slate-700 text-slate-200" onClick={exportCSV}>Export CSV</button>
          </div>
        </header>

        {/* Main Layout */}
        <main className="mt-6 grid md:grid-cols-3 gap-6">
          {/* Calendar */}
          <section className="md:col-span-2 bg-slate-900 rounded-2xl p-4 border border-slate-800 shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button className="px-2 py-1 rounded" onClick={prevMonth}><IconBack /></button>

                {/* Month Selector */}
                <select
                  value={yearMonth.m}
                  onChange={(e) => setYearMonth({ y: yearMonth.y, m: parseInt(e.target.value) })}
                  className="bg-slate-800 text-slate-100 rounded px-2 py-1 text-sm border border-slate-700"
                >
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i} value={i}>
                      {new Date(0, i).toLocaleString(undefined, { month: "long" })}
                    </option>
                  ))}
                </select>

                {/* Year Selector */}
                <select
                  value={yearMonth.y}
                  onChange={(e) => setYearMonth({ y: parseInt(e.target.value), m: yearMonth.m })}
                  className="bg-slate-800 text-slate-100 rounded px-2 py-1 text-sm border border-slate-700"
                >
                  {Array.from({ length: 21 }, (_, i) => {
                    const yr = new Date().getFullYear() - 10 + i;
                    return (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    );
                  })}
                </select>

                <button className="px-2 py-1  rounded" onClick={nextMonth}><IconNext /></button>
              </div>

              <div className="text-sm text-slate-400">JOURNEY TOWARD DREAMS 👑</div>
            </div>

            <div className="mt-4">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="text-xs text-slate-400">
                    <th className="py-2">Sun</th><th className="py-2">Mon</th><th className="py-2">Tue</th><th className="py-2">Wed</th><th className="py-2">Thu</th><th className="py-2">Fri</th><th className="py-2">Sat</th>
                  </tr>
                </thead>
                <tbody>
                  {monthGrid.map((week, wi) => (
                    <tr key={wi}>
                      {week.map((d, di) => {
                        if (!d) return <td key={di} className="h-20 align-top p-1"></td>;
                        const dateStr = formatDateLocal(d);
                        const dayObj = getDayByDate(dateStr);
                        const pl = dayObj ? computeDayPL(dayObj) : 0;
                        const isPositive = pl > 0;
                        const isNegative = pl < 0;

                        // subtle shading: very light green (profit) or very light red (loss)
                        const cellBg = dayObj
                          ? isPositive
                            ? `linear-gradient(180deg, rgba(16,185,129,0.06), #0b1220)`
                            : isNegative
                              ? `linear-gradient(180deg, rgba(239,68,68,0.06), #0b1220)`
                              : "#0b1220"
                          : "#0b1220";

                        return (
                          <td key={di} className="align-top p-1">
                            <div
                              onClick={() => {
                                const existing = getDayByDate(dateStr);
                                if (!existing) createDay(dateStr);
                                setSelectedDate(dateStr);
                              }}
                              className={`cursor-pointer p-2 rounded-lg h-20 flex flex-col justify-between border ${selectedDate === dateStr ? "ring-2 ring-indigo-500" : "border-slate-800"}`}
                              style={{ background: cellBg }}
                            >
                              <div className="flex justify-between items-start">
                                <div className="text-sm font-medium text-slate-200">{d.getDate()}</div>
                                {dayObj && (
  <div className="text-xs text-slate-400">
    {(dayObj.trades || []).length} {((dayObj.trades || []).length === 1 ? "Trade" : "Trades")}
  </div>
)}
                              </div>
                              <div className={`text-sm rounded px-1 py-0.5 text-right ${dayObj ? (isPositive ? "text-emerald-300" : (isNegative ? "text-rose-300" : "text-amber-200")) : "text-slate-600"}`}>
                                {dayObj ? (pl >= 0 ? `+$${pl}` : `$${pl}`) : ""}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Summary + Pie */}
          <aside className="bg-slate-900 rounded-2xl p-4 border border-slate-800 shadow">
            <div className="text-sm text-slate-400">Summary</div>
            <div className="mt-3 grid gap-2">
              <div className="flex items-center justify-between">
                <div>Total P&L</div>
                <div className={`font-mono ${stats.totalPL > 0 ? "text-emerald-300" : stats.totalPL < 0 ? "text-rose-300" : "text-slate-200"}`}>
                  {stats.totalPL >= 0 ? `+$${stats.totalPL}` : `$${stats.totalPL}`}
                </div>
              </div>

              {/* NEW: Acc Balance (end balance of latest day) */}
              <div className="flex items-center justify-between">
                <div>Acc Balance</div>
                <div className="font-mono">{`$${stats.accBalance}`}</div>
              </div>

              <div className="flex items-center justify-between">
                <div>Total trades</div>
                <div>{stats.totalTrades}</div>
              </div>
              <div className="flex items-center justify-between">
                <div>Wins / Losses / Breakeven</div>
                <div>{stats.wins} / {stats.losses} / {stats.breakeven}</div>
              </div>
              <div className="flex items-center justify-between">
                <div>Win rate</div>
                <div>{stats.accuracy}%</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="h-36 mt-20">
                <div className="text-sm font-medium">Win/Loss distribution</div>
                <Pie data={pieData} options={{ plugins: { legend: { position: "bottom" } }, maintainAspectRatio: false }} />
              </div>
            </div>
          </aside>
        </main>

        {/* P&L Graph below calendar */}
        <div className="mt-6 bg-slate-900 rounded-2xl p-4 border border-slate-800 shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">P&L over time</div>
            <div className="flex gap-2 text-xs">
              {["1d", "7d", "30d", "month", "year", "5y", "all"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setInterval(opt)}
                  className={`px-2 py-1 rounded border ${interval === opt ? "bg-indigo-600 border-indigo-600" : "border-slate-700"}`}
                >
                  {opt === "1d"
                    ? "1D"
                    : opt === "7d"
                    ? "7D"
                    : opt === "30d"
                    ? "30D"
                    : opt === "month"
                    ? "1M"
                    : opt === "year"
                    ? "1Y"
                    : opt === "5y"
                    ? "5Y"
                    : "ALL"}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <Line
              data={lineData}
              options={{
                plugins: { legend: { display: false } },
                maintainAspectRatio: false,
              }}
            />
          </div>
        </div>

        {/* Day Detail Modal */}
        {selectedDate && <DayDetail dateStr={selectedDate} onClose={() => setSelectedDate(null)} />}

       {/* Image modal (on top of DayDetail) */}
{imageModal && (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80">
    <img
      src={imageModal.src}
      alt={imageModal.alt}
      className="max-w-[90%] max-h-[90%] rounded shadow-xl"
    />
    <button
      onClick={() => setImageModal(null)}
      className="absolute top-4 right-4 text-white text-2xl"
    >
      ✕
    </button>
  </div>
)}

      </div>
    </div>
  );
}
